/*
 * providers
 */
import { QueryClient } from "@tanstack/react-query"
import { AuthProvider, supabase } from "@psychology/auth/client"
import { DevProvider } from "@psychology/dev"
import { ThemeProvider } from "@psychology/theme"
import { ErrorBoundary, NetworkToast } from "@psychology/react-components"
import { Toaster } from "@psychology/react-ui"
import { TrpcProvider } from "@psychology/trpc/client"
import i18n from "i18next"
import { type ReactNode, useEffect } from "react"
import { trpc, trpcClient } from "./trpc"

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 0, // 1000 * 60, // 1 minute
			retry: 0,
			refetchOnWindowFocus: false, // Optional: disable refetch on window focus
		},
	},
})

type ProvidersProps = {
	children: ReactNode
}

export default function Providers({ children }: ProvidersProps) {
	useEffect(() => {
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((_event, session) => {
			const locale = session?.user?.user_metadata?.locale
			if (locale && i18n.language !== locale) {
				i18n.changeLanguage(locale)
			}
		})
		return () => subscription.unsubscribe()
	}, [])

	useEffect(() => {
		if (!("serviceWorker" in navigator)) {
			return
		}

		function handleMessage(event: MessageEvent) {
			if (event.data?.type !== "PUSH_NOTIFICATION") {
				return
			}
			if (event.data?.payload?.eventType === "role_updated") {
				supabase.auth.refreshSession()
			}
		}

		navigator.serviceWorker.addEventListener("message", handleMessage)
		return () => {
			navigator.serviceWorker.removeEventListener("message", handleMessage)
		}
	}, [])

	return (
		<ErrorBoundary>
			<AuthProvider>
				<TrpcProvider
					trpc={trpc}
					trpcClient={trpcClient}
					queryClient={queryClient}
				>
					<DevProvider>
						<ThemeProvider>
							{children}
							<Toaster />
							<NetworkToast />
						</ThemeProvider>
					</DevProvider>
				</TrpcProvider>
			</AuthProvider>
		</ErrorBoundary>
	)
}
